/* PLUMPLO — storefront page logic (theme-owned).
 *
 * Replaces the prototype's React runtime. Owns: header chrome height, cart count,
 * the hero Creator state (upload → <plumpy-visualiser> → options → add to cart),
 * the example carousel, the size/plumpness pills wherever they appear, the buy box,
 * the sticky bar and the size scene.
 *
 * It talks to the Creator ONLY through the agreed seam: the <plumpy-visualiser>
 * attributes (image, plumpness, size, bleed-mode, bleed-color, reverse-text,
 * reverse-font, back-print), el.resetView(), and the cutoutready / bleedpalette /
 * plumplo-bg-status events. It never reaches into the component, and never names a provider.
 *
 * Order data: the cart line carries Border, Back print, Message, Message font, Notes and the opaque
 * _design_id issued by the design service. No artwork URL ever reaches Shopify.
 */
(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.prototype.slice.call((r || document).querySelectorAll(s));
  const on = (el, ev, fn, o) => el && el.addEventListener(ev, fn, o);

  /* ---- header chrome + cart count (every page) ---------------------------- */
  function measureChrome() {
    const hg = $('#header-group') || $('[data-plp-header]');
    if (!hg) return;
    const h = Math.round(hg.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--plp-chrome', h + 'px');
    /* --plp-chrome is the FULL header group, which on this theme includes strips (announcement
       bar, utility row) that scroll away. Anything that pins itself while the page scrolls — the
       sticky left gallery, in-page section links — must clear only the part that actually stays
       on screen. That is the resting bottom edge of the sticky/fixed elements inside the group:
       each one's own CSS `top` plus its height. If nothing in the header is sticky the header
       scrolls away completely and the clearance is zero. */
    let stick = 0;
    const consider = (el) => {
      const cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
      const t = parseFloat(cs.top);
      const bottom = (isFinite(t) ? t : 0) + el.getBoundingClientRect().height;
      if (bottom > stick) stick = bottom;
    };
    consider(hg);
    /* PLUMPLO's own header markup is #header-group > section wrapper > .plp[data-plp-header] >
       (.plp-announce, header.plp-header), so scan from the PLUMPLO wrapper when it is there and
       fall back to two levels of the group otherwise. */
    const inner = $('[data-plp-header]', hg) || hg;
    $$(':scope > *, :scope > * > *', inner).forEach(consider);
    document.documentElement.style.setProperty('--plp-stick', Math.round(Math.min(stick, h)) + 'px');
  }
  /* The product gallery's square must be completely visible, with breathing room under it, at the
     top of the page — before anything is scrolled. What limits it there is not the sticky header
     but the gallery's own resting position: header group (announcement bar included, since that is
     still on screen at the top of the page), breadcrumb, section padding. Measure that once from
     the offsetTop chain, which sticky positioning does not affect, and publish what is left as
     --plp-gavail. The stylesheet caps the square's WIDTH with it, so 1:1 is preserved and the
     square shrinks only by as much as the viewport actually demands. */
  const GALLERY_BOTTOM = 28;                               // deliberate breathing room under the square
  const GALLERY_MIN = 320;                                 // never shrink past usable on a very short window
  function measureGallery() {
    const g = $('[data-plp-gallery]');
    if (!g) return;
    /* Measure the square itself, not its wrapper: the wrapper carries the sticky lead padding, and
       counting from the wrapper would spend the breathing room on that padding instead. */
    let top = 0;
    for (let el = $('.plp-pdp__stage', g) || g; el; el = el.offsetParent) top += el.offsetTop || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!vh) return;
    const avail = Math.max(GALLERY_MIN, Math.round(vh - top - GALLERY_BOTTOM));
    document.documentElement.style.setProperty('--plp-gavail', avail + 'px');
  }
  function refreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => {
      $$('[data-plp-cart-count]').forEach((b) => { b.textContent = c.item_count; b.dataset.count = c.item_count; });
      noteCartLines(c.items);
    }).catch(() => {});
  }

  /* ---- personalised cart thumbnails (browser-only) ------------------------------
     At Add to Cart the product page snapshots the Plumplo the Creator is already showing and
     keeps a 240px copy in this browser's localStorage, keyed by the line's _design_id. Whenever
     the cart drawer / cart page shows a line with a matching id, that snapshot replaces the
     generic product image in Horizon's own thumbnail slot. Nothing leaves the browser: no
     artwork is uploaded, nothing is added to the cart line, and another device (or cleared
     storage) simply sees Shopify's product image. */
  const PREVIEWS_KEY = 'plumplo:previews';
  const keyToDesign = {};
  /* The exact configuration on each line. Two lines from the same photo (say peach and lavender
     borders) share a _design_id but not an _artwork_id, so the thumbnail is looked up by
     _artwork_id first; _design_id remains the fallback for snapshots saved before 30 Sep 2026. */
  const keyToArtwork = {};
  const keyToImage = {};                                   // Shopify's own line image, when the product has one
  /* Every line key /cart.js has reported, Plumplo or not. Shopify re-keys a line when a discount
     code is applied or removed, so the drawer can re-render with a key this map has never seen;
     applyCartPreviews then asks /cart.js once to learn it. Each unfamiliar key triggers at most
     one lookup, so this cannot loop. */
  const keysKnown = new Set();
  const keysTried = new Set();
  let relearning = false;
  function noteCartLines(items) {
    (items || []).forEach((it) => {
      if (!it || !it.key) return;
      keysKnown.add(it.key);
      const id = it.properties && it.properties._design_id;
      if (id) keyToDesign[it.key] = id;
      const art = it.properties && it.properties._artwork_id;
      if (art) keyToArtwork[it.key] = art;
      const img = it.image || (it.featured_image && it.featured_image.url) || '';
      if (img) keyToImage[it.key] = img;
    });
    applyCartPreviews();
  }
  function previewsRead() { try { return JSON.parse(localStorage.getItem(PREVIEWS_KEY) || '{}') || {}; } catch (e) { return {}; } }
  function previewSave(id, dataUrl) {
    try {
      const m = previewsRead(); m[id] = { d: dataUrl, t: Date.now() };
      Object.keys(m).sort((a, b) => m[b].t - m[a].t).slice(6).forEach((k) => { delete m[k]; });
      localStorage.setItem(PREVIEWS_KEY, JSON.stringify(m));
    } catch (e) { /* storage unavailable or full: the generic image stays */ }
  }
  /* Thumbnail hierarchy for every cart line, in the drawer and on /cart:
       1. the personalised browser-side preview stored for this line's _design_id;
       2. otherwise Shopify's own line image, whenever the product has one;
       3. otherwise a neutral PLUMPLO tile, drawn in CSS, so the row stays tidy instead of
          showing an empty cell or a broken image.
     Tier 2 needs no work when Horizon has already rendered the image; it only has to be built
     when the cell is empty. Adding product media in admin therefore takes over from the tile
     automatically. */
  function cartMediaImg(cell, row) {
    const a = document.createElement('a'); a.className = 'cart-items__media-container'; a.style.setProperty('--ratio', '1');
    const t = $('.cart-items__title', row); if (t && t.getAttribute('href')) a.href = t.getAttribute('href');
    const img = document.createElement('img'); img.className = 'cart-items__media-image'; img.alt = '';
    a.appendChild(img); cell.appendChild(a);
    return img;
  }
  /* Every personalised cart line points at its OWN design, so opening a line from the cart resumes
     that Plumplo rather than whatever was configured most recently. Re-running is safe: set()
     replaces an existing design parameter rather than appending one. */
  function designHref(base, id, key) {
    try {
      const u = new URL(base, location.origin);
      u.searchParams.set('design', id);
      if (key) u.searchParams.set('pp_line', key); else u.searchParams.delete('pp_line');
      return u.pathname + u.search;
    }
    catch (e) { return base; }
  }
  function linkCartLine(row, id) {
    if (!id) return;
    /* The line key travels with the design id so an edit trip knows which line it came from.
       Without it the replacement can only be added, never swapped. */
    const key = row.dataset.key || '';
    $$('a[href]', row).forEach((a) => {
      const h = a.getAttribute('href');
      if (h && h.indexOf('/products/') > -1) a.setAttribute('href', designHref(h, id, key));
    });
  }
  function applyCartPreviews() {
    const rows = $$('.cart-items__table-row[data-key]');
    if (!rows.length) return;
    const unknown = rows.map((r) => r.dataset.key).filter((k) => !keysKnown.has(k) && !keysTried.has(k));
    if (!relearning && unknown.length) {
      relearning = true;
      unknown.forEach((k) => keysTried.add(k));             // each unfamiliar key is looked up once
      fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())
        .then((c) => { relearning = false; noteCartLines(c.items); }, () => { relearning = false; });
    }
    const m = previewsRead();
    rows.forEach((row) => {
      const cell = $('.cart-items__media', row); if (!cell) return;
      const id = keyToDesign[row.dataset.key];
      linkCartLine(row, id);
      const art = keyToArtwork[row.dataset.key];
      const pid = (art && m[art]) ? art : id;
      const p = pid && m[pid];
      let img = $('img', cell);
      if (p) {
        if (img && img.dataset.plpPreview === pid) return;
        if (!img) img = cartMediaImg(cell, row);
        cell.classList.remove('plp-cart-ph');
        img.removeAttribute('srcset'); img.removeAttribute('sizes');
        img.src = p.d; img.dataset.plpPreview = pid;
        return;
      }
      if (img) { cell.classList.remove('plp-cart-ph'); return; }   // Shopify's own image is already there
      const fallback = keyToImage[row.dataset.key];
      if (fallback) {
        img = cartMediaImg(cell, row);
        img.src = fallback; img.dataset.plpPreview = 'shopify';
        cell.classList.remove('plp-cart-ph');
        return;
      }
      cell.classList.add('plp-cart-ph');
    });
  }
  let previewT = null;
  const previewsSoon = () => { clearTimeout(previewT); previewT = setTimeout(applyCartPreviews, 60); };
  on(document, 'DOMContentLoaded', () => {
    new MutationObserver(previewsSoon).observe(document.body, { childList: true, subtree: true });
    applyCartPreviews();
  });
  on(document, 'shopify:cart:lines-update', (e) => { if (e.promise) e.promise.then(() => refreshCartCount(), () => {}); });
  const measureLayout = () => { measureChrome(); measureGallery(); };
  on(window, 'resize', measureLayout, { passive: true });
  on(document, 'DOMContentLoaded', measureLayout);
  on(window, 'load', measureLayout);
  measureLayout();
  on(document, 'DOMContentLoaded', refreshCartCount);

  /* Smooth in-page scrolling for nav / CTA anchors that point at this page.
     Horizon scrolls `.page-wrapper`, not the window, once that element is the overflow container
     (desktop widths), so window.scrollTo silently does nothing there — the section links stopped
     working for exactly that reason. Resolve the real scroll box each time, and take the header
     clearance from the measured --plp-chrome rather than a fixed number. */
  function scrollBox() {
    const pw = document.querySelector('.page-wrapper');
    if (pw && pw.scrollHeight > pw.clientHeight + 1) {
      const oy = getComputedStyle(pw).overflowY;
      if (oy === 'auto' || oy === 'scroll') return pw;
    }
    return document.scrollingElement || document.documentElement;
  }
  function headerGap() {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--plp-chrome'));
    return (v > 0 ? v : 68) + 12;
  }
  function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return false;
    const box = scrollBox();
    const root = document.scrollingElement || document.documentElement;
    const base = box === root ? 0 : box.getBoundingClientRect().top;
    const top = el.getBoundingClientRect().top - base + box.scrollTop - headerGap();
    box.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    return true;
  }
  /* header "Start with a photo": go to the hero upload control (or the Creator once a
     photo is loaded) and hand it keyboard focus. Off the homepage the link navigates. */
  function toUpload() {
    const input = $('#hero-upload');
    if (!input) return false;
    const label = $('.plp-upload label[for="hero-upload"]');
    const visible = !!(label && label.offsetParent);
    const creator = $('.plp-pdp__creator');               // product page: Creator stage + upload card
    const target = creator || (visible ? label : ($('[data-plp-mount]') || input.closest('[data-plp-hero]')));
    if (!target) return false;
    target.scrollIntoView({ block: (visible && !creator) ? 'center' : 'start', behavior: 'smooth' });
    if (visible || creator) { try { input.focus({ preventScroll: true }); } catch (err) { /* ignore */ } }
    return true;
  }
  on(document, 'click', (e) => {
    const a = e.target.closest('a[data-plp-to-upload]');
    if (a && toUpload()) e.preventDefault();
  });
  /* Arriving from another page with #section in the URL (the nav links off the PDP and cart do
     exactly that): the browser's own hash jump targets the window, which is not the scroll
     container here, so land the section ourselves once layout has settled. */
  on(window, 'load', () => {
    const h = location.hash;
    if (!h || h.length < 2) return;
    if (h === '#hero-upload') { setTimeout(toUpload, 50); return; }
    setTimeout(() => scrollToId(h.slice(1)), 60);
  });
  on(document, 'click', (e) => {
    if (e.defaultPrevented) return;
    const a = e.target.closest('a[href*="#"]');
    if (!a) return;
    const url = new URL(a.getAttribute('href'), location.href);
    if (url.pathname !== location.pathname || !url.hash) return;
    if (scrollToId(url.hash.slice(1))) e.preventDefault();
  });

  /* ---- page state ------------------------------------------------------------ */
  const cfgEl = $('[data-plp-config]');
  if (!cfgEl) return;                                     // no Creator on this page
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  const S = {
    photo: null, file: null, designId: null, real: false, busy: false,
    size: 1, plump: 0, qty: 1, addon: false, sizeRef: true,
    bleedMode: 'color', bleedColor: '#FFFFFF', notes: '',
    backPrint: true, showMessage: false, reverseText: cfg.defaultMessage || 'Dear Jane, I love you. John',
    msgFont: 'elegant',
    cart: 'idle', slide: 0, held: false, sticky: false
  };
  /* The five reverse-message typefaces. `family` is handed to the Creator as the reverse-font
     attribute (it becomes the canvas font shorthand's family, so the quoting matters) and is the
     same string the picker chips are drawn in, which is also what makes the browser download the
     webfont before the canvas ever needs it. `label` is what production sees on the order.
     An unknown or missing key falls back to elegant, so an older saved design is unchanged. */
  const FONTS = [
    { key: 'elegant',     label: 'Elegant — Great Vibes',      family: '"Great Vibes", cursive' },
    { key: 'plumpy',      label: 'Plumpy — Fredoka',           family: '"Fredoka", ui-rounded, sans-serif' },
    { key: 'bold',        label: 'Bold — Bebas Neue',          family: '"Bebas Neue", Impact, sans-serif' },
    { key: 'classic',     label: 'Classic — DM Serif Display', family: '"DM Serif Display", Georgia, serif' },
    { key: 'handwritten', label: 'Handwritten — Caveat',       family: '"Caveat", cursive' }
  ];
  const fontFor = (k) => FONTS.filter((f) => f.key === k)[0] || FONTS[0];
  const sizes = cfg.sizes || [];
  const sym = (cfg.moneySample || '$0.00').replace(/[\d.,\s]/g, '').replace(/[A-Z]{3}$/, '') || '$';
  const placeholder = !!cfg.pricingPlaceholder;
  /* Which page this is. On the homepage the CTA hands the design over to the product page;
     on the product page it adds to cart. */
  const isPDP = cfg.page === 'product';
  const pdpUrl = cfg.pdpUrl || '';

  /* ---- design hand-off (homepage → product page) ------------------------------
     The cutout the design service returned is kept in the browser (IndexedDB) together
     with the design id and the customer's settings, so the product page can resume the
     design without another background-removal call: the stored cutout already carries the
     matte, so the Creator treats it as a pre-cut upload and never contacts the service. */
  /* Designs are stored one record per _design_id, so several completed Plumplos coexist and a cart
     line can be reopened long after a newer photo was uploaded. `current` holds only a pointer to
     the design being worked on. `ttl` is how long that pointer auto-resumes for; `keep` is how long
     the records themselves survive, which has to outlast a cart. */
  const DB = { name: 'plumplo', store: 'design', key: 'current', ttl: 24 * 3600 * 1000, keep: 30 * 24 * 3600 * 1000 };
  function dbOpen() {
    return new Promise((res, rej) => {
      if (!window.indexedDB) return rej(new Error('no idb'));
      const r = indexedDB.open(DB.name, 1);
      r.onupgradeneeded = () => { r.result.createObjectStore(DB.store); };
      r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
    });
  }
  function dbPut(key, val) {
    return dbOpen().then((db) => new Promise((res, rej) => {
      const tx = db.transaction(DB.store, 'readwrite'); tx.objectStore(DB.store).put(val, key);
      tx.oncomplete = () => res(true); tx.onerror = () => rej(tx.error);
    })).catch(() => false);
  }
  function dbGet(key) {
    return dbOpen().then((db) => new Promise((res, rej) => {
      const r = db.transaction(DB.store).objectStore(DB.store).get(key);
      r.onsuccess = () => res(r.result || null); r.onerror = () => rej(r.error);
    })).catch(() => null);
  }
  function dbDel(key) {
    return dbOpen().then((db) => new Promise((res) => {
      const tx = db.transaction(DB.store, 'readwrite'); tx.objectStore(DB.store).delete(key);
      tx.oncomplete = () => res(true); tx.onerror = () => res(false);
    })).catch(() => false);
  }
  /* Housekeeping only: drop records past `keep`. Never touches live designs. */
  function dbPrune() {
    return dbOpen().then((db) => new Promise((res) => {
      const store = db.transaction(DB.store, 'readwrite').objectStore(DB.store);
      const r = store.getAllKeys();
      r.onsuccess = () => {
        const now = Date.now();
        (r.result || []).forEach((k) => {
          if (k === DB.key) return;
          const g = store.get(k);
          g.onsuccess = () => { const v = g.result; if (v && v.ts && now - v.ts > DB.keep) store.delete(k); };
        });
        res(true);
      };
      r.onerror = () => res(false);
    })).catch(() => false);
  }
  let cutoutBlob = null;                                   // the trusted cutout, as a PNG blob
  function settingsSnapshot() {
    return { size: S.size, plump: S.plump, bleedMode: S.bleedMode, bleedColor: S.bleedColor, backPrint: S.backPrint,
      showMessage: S.showMessage, reverseText: S.reverseText, msgFont: S.msgFont, notes: S.notes, qty: S.qty, addon: S.addon };
  }
  function saveDesign() {
    if (!S.designId || !cutoutBlob) return Promise.resolve(false);
    const rec = { designId: S.designId, cutout: cutoutBlob, settings: settingsSnapshot(), ts: Date.now() };
    return dbPut(S.designId, rec).then(() => dbPut(DB.key, { designId: rec.designId, ts: rec.ts }));
  }

  function variantFor(si, pi) {
    return (cfg.variants || []).find((v) => v.size === si && v.plump === pi) || null;
  }
  function priceLabel() {
    if (placeholder) return sym + '——';
    const v = variantFor(S.size, S.plump);
    return v ? v.price : sym + '——';
  }
  const hasPhoto = () => !!S.photo;

  /* ---- elements ------------------------------------------------------------ */
  const hero = $('[data-plp-hero]');
  const mount = $('[data-plp-mount]');
  const mount2 = $('[data-plp-mount-2]');                 // homepage buy box: a second, mirrored Creator
  const carousel = $('[data-plp-carousel]');
  const uploads = $$('input[type=file][data-plp-upload]');
  const gPreviewEls = $$('[data-plp-gpreview]');           // product gallery: the customer's own Plumplo
  const gPreviewThumbs = $$('[data-plp-gpreview-thumb]');
  const errorEls = $$('[data-plp-error]');

  function error(msg) { errorEls.forEach((e) => { e.textContent = msg || ''; }); }
  function autogrow(el) { if (!el) return; el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 150) + 'px'; }

  /* ---- the Creator ---------------------------------------------------------- */
  let vis = null, vis2 = null, vis2Url = null;
  function applyAttrs(el) {
    el.setAttribute('plumpness', String(S.plump));
    el.setAttribute('size', String((sizes[S.size] && sizes[S.size].mm) || 300));
    el.setAttribute('bleed-mode', S.bleedMode);
    el.setAttribute('bleed-color', S.bleedColor);
    el.setAttribute('reverse-text', S.showMessage ? S.reverseText : '');
    el.setAttribute('reverse-font', fontFor(S.msgFont).family);
    el.setAttribute('back-print', S.backPrint ? '1' : '0');
  }
  /* A webfont the page has not painted yet has not been downloaded yet, and canvas silently
     falls back to the generic family instead of waiting. All five are requested once at start-up
     (they are small, and the picker chips need them anyway); this covers the rare case where the
     chosen one is still in flight by re-pushing the attribute through a cleared value, which is
     the only way to make the Creator notice a family it has already been told about. */
  const fontSpec = (key) => '400 40px ' + fontFor(key).family;
  function ensureFont(key) {
    if (!document.fonts || !document.fonts.check) return;
    try {
      if (document.fonts.check(fontSpec(key))) return;
      document.fonts.load(fontSpec(key)).then(() => {
        if (S.msgFont !== key) return;
        [vis, vis2].forEach((el) => { if (el) el.setAttribute('reverse-font', ''); });
        visAttrs();
        galleryPreviewRefresh(600);
      }, () => {});
    } catch (e) { /* font loading is a nicety — never let it break the Creator */ }
  }
  try { if (document.fonts && document.fonts.load) FONTS.forEach((f) => document.fonts.load(fontSpec(f.key))); } catch (e) {}
  function visAttrs() {
    if (vis) applyAttrs(vis);
    if (vis2) applyAttrs(vis2);
  }
  /* The mirrored Creator is fed the finished cutout (pre-cut → no service call) so both
     stages show the same pillow and follow the same settings.
     Called before a cutout exists it mounts the SAME component with no image at all, which is
     exactly the state the homepage top stage is in while the background is being removed: the
     Creator draws its blank ghost Plumplo and, having no photo, contacts no service. The ghost
     becomes the customer's Plumplo in place as soon as this is called again with the cutout. */
  function mountMirror() {
    if (!mount2) return;
    if (!vis2) {
      vis2 = document.createElement('plumpy-visualiser');
      const anchor = $('[data-plp-mount-anchor]', mount2);
      if (anchor) mount2.insertBefore(vis2, anchor.nextSibling); else mount2.appendChild(vis2);
    }
    applyAttrs(vis2);
    if (!cutoutBlob) { vis2.removeAttribute('image'); return; }
    if (vis2Url) URL.revokeObjectURL(vis2Url);
    vis2Url = URL.createObjectURL(cutoutBlob);
    vis2.setAttribute('image', vis2Url);
  }
  function unmountMirror() {
    if (vis2) { vis2.remove(); vis2 = null; }
    if (vis2Url) { URL.revokeObjectURL(vis2Url); vis2Url = null; }
  }
  function mountVisualiser() {
    if (!mount || !S.photo) return;
    if (!vis) {
      vis = document.createElement('plumpy-visualiser');
      const anchor = $('[data-plp-mount-anchor]', mount);
      if (anchor) mount.insertBefore(vis, anchor.nextSibling); else mount.appendChild(vis);
      on(vis, 'cutoutready', onCutoutReady);
    }
    visAttrs();
    vis.setAttribute('image', S.photo);
  }
  function unmountVisualiser() {
    if (vis) { vis.remove(); vis = null; }
    unmountMirror();
  }
  /* Read the trusted cutout back out of the Creator (a canvas from the service, or the
     pre-cut image itself) as a PNG blob, for the mirror and the hand-off. */
  function captureCutout() {
    return new Promise((res) => {
      const c = vis && vis._cutout;
      if (!c) return res(null);
      if (c.toBlob) return c.toBlob((b) => res(b || null), 'image/png');
      if (c instanceof HTMLImageElement) {
        if (resumedBlob) return res(resumedBlob);
        try { const cv = document.createElement('canvas'); cv.width = c.naturalWidth; cv.height = c.naturalHeight; cv.getContext('2d').drawImage(c, 0, 0); cv.toBlob((b) => res(b || null), 'image/png'); } catch (e) { res(null); }
        return;
      }
      res(null);
    });
  }
  let resumedBlob = null;
  function onCutoutReady(e) {
    const d = e.detail || {};
    S.real = !!d.real;
    if (!S.real) {
      S.busy = false;
      /* No Plumplo was built, so the ghost must not linger as if one were coming. */
      busyVeil(false); unmountMirror(); galleryPreviewHide();
      error(cfg.msgUnavailable || 'BACKGROUND REMOVAL IS UNAVAILABLE RIGHT NOW — PLEASE TRY AGAIN SHORTLY');
      render();
      return;
    }
    error('');
    /* The ghost holds the stage until the cutout is in hand; mountMirror() then swaps it for the
       customer's Plumplo in the same component, so there is no empty frame between the two. */
    captureCutout().then((b) => { cutoutBlob = b; mountMirror(); busyVeil(false); saveDesign(); });
    galleryPreviewRefresh(800);
    /* Pre-cut uploads never pass through the provider, so the order would have no
       design record. Store the original now to get one. */
    if (d.provider === 'supplied' && !S.designId && window.PlumploBG && window.PlumploBG.storeSupplied) {
      const img = new Image(); img.src = S.photo;
      S.busy = true; render();
      window.PlumploBG.storeSupplied(img).then((id) => { S.designId = id || null; S.busy = false; render(); saveDesign(); });
    } else {
      S.busy = false; render();
    }
  }
  on(document, 'plumplo-bg-status', (e) => {
    const d = e.detail || {};
    if (d.designId) S.designId = d.designId;
    if (d.stage === 'uploading' || d.stage === 'segmenting') S.busy = true;
    if (d.stage === 'done' || d.stage === 'stored' || d.stage === 'store-failed') S.busy = false;
    render();
  });
  on(document, 'bleedpalette', (e) => { S.palette = (e.detail && e.detail.suggestions) || []; });

  /* ---- upload / remove ---------------------------------------------------- */
  /* Choosing a photo re-lays out the whole page: the hero swaps its example carousel for the
     Creator, every [data-plp-if-photo] / [data-plp-if-nophoto] pair flips, and sections above the
     viewport change height. The scroll container keeps its scrollTop through all of that, so a
     customer who uploaded from the buy box near the bottom of the page was left looking at a
     different part of it — or past the end, at nothing but the sticky bar. Anchor on the block the
     customer is actually working in and put the scroll position back where it was relative to it. */
  function keepInPlace(anchor, work) {
    if (!anchor) { work(); return; }
    const box = scrollBox();
    const before = anchor.getBoundingClientRect().top;
    work();
    const shift = anchor.getBoundingClientRect().top - before;
    if (Math.abs(shift) > 1) box.scrollTop += shift;
  }
  function setPhoto(file, source) {
    if (!file) return;
    if (S.photo) URL.revokeObjectURL(S.photo);
    S.photo = URL.createObjectURL(file); S.file = file;
    S.designId = null; S.real = false; S.busy = true; S.cart = 'idle';
    cutoutBlob = null; resumedBlob = null; lastPreviewUrl = null; unmountMirror();
    error('');
    /* Can this browser open the photo at all? An unreadable file (an iPhone HEIC on most Android
       browsers, or a photo too large for the phone to decode) otherwise leaves the Creator stuck on
       "Removing background…" with no word to the customer. Display check only: the file, the
       design id and the artwork are untouched. */
    const probeUrl = S.photo, probe = new Image();
    probe.onload = () => { probe.onload = probe.onerror = null; probe.src = ''; };
    probe.onerror = () => { probe.onload = probe.onerror = null; if (S.photo === probeUrl) photoUnreadable(source); };
    probe.src = probeUrl;
    /* The file input itself is visually hidden, so anchor on the configuration block around it. */
    const anchor = source && source.closest
      ? source.closest('.plp-buy__box, .plp-pdp__panel, .plp-upload, [data-plp-hero]')
      : null;
    keepInPlace(anchor, () => {
      render();
      mountVisualiser();
      showProcessing();
    });
  }
  /* Every upload — first, "Change photo", and every one after that — shows the customer that their
     photo is being worked on, on the surface they are actually looking at.

     The Creator paints its own processing state (blank ghost Plumplo under a cream wash carrying
     "Removing background…") inside its own stage, and only the instance doing the work paints it.
     The homepage top stage is that instance, which is why it has always been right; the other two
     surfaces are mirror stages and had nothing.

     Product page: the working Creator is deliberately collapsed to zero height, so its ghost is
     clipped with it. The gallery's live slide takes over — the mirror component with no image is
     the same ghost, and it costs no service call — and the wash goes over it, so the treatment
     matches the homepage rather than being a bright, unwashed pillow.

     Homepage buy box: the top Creator is already showing the real ghost a screen above, and a
     SECOND live renderer here while the first is mid-pipeline is what broke the page, so this
     stage gets the wash and the status text only. One renderer per page while processing, exactly
     as before the regression. */
  function showProcessing() {
    try {
      if (isPDP && gPreviewEls.length) {
        clearTimeout(gT);
        gShown = true;
        gPreviewThumbs.forEach((im) => im.removeAttribute('src'));   // the previous design's snapshot is not this one
        galleryPreviewShow('', true);
        mountMirror();
      }
      busyVeil(true);
    } catch (e) { /* feedback must never be able to break the upload itself */ }
  }
  /* The Creator's own wash, reproduced over the mirror stage: same cream, same blur, same mono
     caption, same words. It is a plain element inside the stage, so it cannot affect anything
     outside it. */
  function busyVeil(on) {
    if (!mount2) return;
    const el = $('.plp-busy', mount2);
    if (!on) { if (el) el.remove(); return; }
    if (el) return;
    const v = document.createElement('div');
    v.className = 'plp-busy';
    v.setAttribute('role', 'status');
    v.textContent = 'Removing background…';
    mount2.appendChild(v);
  }
  /* Resume the shared design on whichever surface is being viewed — the homepage Creator, the
     homepage buy box (fed by the same state through the mirror) or the product page. The stored
     cutout is already matted, so the Creator treats it as a pre-cut upload: no second
     background-removal call and no new design id. */
  function applyDesign(d) {
    Object.assign(S, d.settings || {});
    S.designId = d.designId; S.real = false; S.busy = true; S.cart = 'idle';
    cutoutBlob = d.cutout; resumedBlob = d.cutout;
    if (S.photo) URL.revokeObjectURL(S.photo);
    S.photo = URL.createObjectURL(d.cutout); S.file = null;
    render();
    mountVisualiser();
  }
  function resumeDesign() {
    if (!mount) return;
    const wanted = new URLSearchParams(location.search).get('design');
    /* A requested id wins outright — a cart line links to its own line's _design_id, and opening it
       must reopen that Plumplo however many newer ones exist. The `current` pointer is consulted
       only when nothing was asked for. Old single-record stores are read as-is (migration). */
    const load = wanted
      ? dbGet(wanted)
      : dbGet(DB.key).then((p) => {
          if (!p) return null;
          if (p.cutout) return p;                              // legacy fixed 'current' record
          if (!p.designId) return null;
          if ((Date.now() - (p.ts || 0)) > DB.ttl) return null; // stale pointer: start fresh
          return dbGet(p.designId);
        });
    load.then((d) => {
      const ok = d && d.designId && d.cutout && (Date.now() - (d.ts || 0)) <= DB.keep && (!wanted || d.designId === wanted);
      if (!ok) {
        /* Never stand in a different design for the one that was asked for. */
        if (wanted) error(cfg.msgDesignGone || 'THAT DESIGN IS NOT AVAILABLE IN THIS BROWSER — PLEASE UPLOAD THE PHOTO AGAIN');
        return;
      }
      applyDesign(d);
      /* A resumed cutout is already matted, so the Creator treats it as pre-cut: no second
         background-removal call and no new design id. */
      if (!wanted && d.designId) dbPut(DB.key, { designId: d.designId, ts: d.ts || Date.now() });
    });
    dbPrune();
  }
  function photoUnreadable(source) {
    /* Back to the no-photo state, but keep any saved design: the customer did nothing wrong. */
    removePhoto(source, { keepSaved: true });
    error(cfg.msgUnreadable || "WE COULDN'T OPEN THAT PHOTO ON THIS DEVICE — PLEASE TRY A JPG OR PNG, OR A SCREENSHOT OF IT");
  }
  function removePhoto(source, opts) {
    if (S.photo) URL.revokeObjectURL(S.photo);
    S.photo = null; S.file = null; S.designId = null; S.real = false; S.busy = false; S.cart = 'idle';
    uploads.forEach((i) => { i.value = ''; });
    cutoutBlob = null; resumedBlob = null; lastPreviewUrl = null;
    /* Removing flips every photo/no-photo block back the other way, so the page changes height
       above the viewport exactly as it does on upload — and left unanchored it carried the
       customer off the bottom of the page, where the next upload had nothing visible to return
       to. Same anchor treatment as setPhoto. */
    const anchor = source && source.closest
      ? source.closest('.plp-buy__box, .plp-pdp__panel, .plp-upload, [data-plp-hero]')
      : null;
    keepInPlace(anchor, () => {
      unmountVisualiser();
      galleryPreviewHide();
      busyVeil(false);
      error('');
      render();
    });
    /* Clears the working design only. The stored per-id records stay: cart lines still link to
       them, and removing the photo you are configuring must not empty the cart's designs. */
    if (!(opts && opts.keepSaved)) dbDel(DB.key);
  }
  /* The sticky bar's "Upload" opens the photo picker straight away (27 Sep 2026). It used to scroll
     the page instead, which on the homepage was a ~15,000 px jump to the buy box that a touch could
     stop halfway. Once a photo is chosen from there, the Creator is brought into view so the
     customer sees their photo being prepared. */
  let pickedFromSticky = false;
  function openPicker() {
    const input = $('#hero-upload');
    if (!input) return false;
    pickedFromSticky = true;
    input.click();
    return true;
  }
  uploads.forEach((i) => on(i, 'change', (e) => {
    const file = e.target.files && e.target.files[0];
    setPhoto(file, e.target);
    if (pickedFromSticky && file) {
      requestAnimationFrame(() => {
        const target = $('.plp-pdp__creator') || $('[data-plp-mount]');
        if (target) target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }
    pickedFromSticky = false;
  }));

  /* ---- cart ----------------------------------------------------------------- */
  let cartT = null;
  function giftWrapVariant() {
    const b = $('[data-plp-addon]');
    const id = b && b.dataset.plpAddon;
    return id && /^\d+$/.test(id) ? +id : null;
  }
  /* ---- replace-the-line on an edit trip ---------------------------------------
     Opening a Plumplo from the cart carries both its design id and its cart line key.
     Coming back with a changed configuration is a REPLACEMENT, not a second pillow:
     the new line is added first, and only once Shopify has confirmed it does the
     superseded line go. Every uncertain case keeps both lines standing — a duplicate
     is a nuisance the customer can fix, deleting the wrong line destroys a design
     they may not be able to rebuild. One replacement per edit trip. */
  let editLineKey = (function () {
    try {
      const q = new URLSearchParams(location.search);
      return (q.get('design') && q.get('pp_line')) ? q.get('pp_line') : null;
    } catch (e) { return null; }
  })();
  function replaceEditedLine(added, designId) {
    const key = editLineKey;
    editLineKey = null;                                    // spent, whatever happens below
    if (!key) return Promise.resolve(null);                // a normal add: never removes anything
    const line = added && added.items && added.items[0];
    if (!line || !line.key) return Promise.resolve(null);  // no confirmed replacement: leave the old line
    if (line.key === key) return Promise.resolve(null);    // nothing changed; Shopify merged into the same line
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const hits = (cart.items || []).filter((it) => it && it.key === key);
        const old = hits.length === 1 ? hits[0] : null;
        /* Both tests must pass: exactly one line with that key, and it is the design we
           were editing. Anything else is ambiguous and both lines stay. */
        if (!old || !old.properties || old.properties._design_id !== designId) return null;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0, sections: CART_DRAWER_SECTION })
        }).then((r) => (r.ok ? r.json() : null), () => null);
      })
      .catch(() => null);                                  // removal never fails the sale
  }
  function addToCart() {
    if (S.cart === 'adding') return;
    if (!hasPhoto()) { if (isPDP) toUpload(); else scrollToId('buy'); return; }
    if (S.busy) { error(cfg.msgBusy || 'STILL PREPARING YOUR PHOTO — ONE MOMENT'); return; }
    if (!S.real) { error(cfg.msgUnavailable || 'BACKGROUND REMOVAL IS UNAVAILABLE RIGHT NOW — PLEASE TRY AGAIN SHORTLY'); return; }
    if (!S.designId) { error(cfg.msgNoDesign || 'WE COULD NOT SAVE YOUR PHOTO — PLEASE UPLOAD IT AGAIN'); return; }
    if (!isPDP && pdpUrl) {
      /* Homepage: carry the design to the product page instead of adding here. */
      S.cart = 'adding'; render();
      /* No pp_line: a homepage handoff is a new Plumplo, never a replacement. */
      const go = () => { location.href = pdpUrl + (pdpUrl.indexOf('?') < 0 ? '?' : '&') + 'design=' + encodeURIComponent(S.designId); };
      (cutoutBlob ? saveDesign() : captureCutout().then((b) => { cutoutBlob = b; return saveDesign(); })).then(go, go);
      return;
    }
    const v = variantFor(S.size, S.plump);
    if (!v || !v.available) { error(cfg.msgNoVariant || 'THIS SIZE AND PLUMPNESS COMBINATION IS NOT AVAILABLE'); return; }
    /* The order line names the border colour — "Pastel yellow (#F5E08A)" for a preset from
       the buy box's swatches, "Custom colour (#ABC123)" for anything else — so the colour
       reads on the order, in emails and on the packing slip. Display only: the hashed
       configuration carries the hex itself and is unchanged. */
    const BORDER_NAMES = {
      '#FFFFFF': 'White', '#F2B8CB': 'Plumplo pastel pink', '#A9DCC0': 'Mint', '#C4B0E8': 'Lavender',
      '#F5E08A': 'Pastel yellow', '#F7BE9A': 'Peach', '#A9D3EF': 'Pastel blue', '#EF8A82': 'Coral', '#111111': 'Black'
    };
    const borderHex = String(S.bleedColor || '').toUpperCase();
    const props = {
      'Border': S.bleedMode === 'none' ? 'No border' : (BORDER_NAMES[borderHex] || 'Custom colour') + ' (' + borderHex + ')',
      'Back print': S.backPrint ? 'Photo' : 'No print',
      '_design_id': S.designId
    };
    if (S.showMessage && S.reverseText.trim()) {
      props['Message'] = S.reverseText.trim();
      props['Message font'] = fontFor(S.msgFont).label;
    }
    if (S.notes.trim()) props['Notes'] = S.notes.trim();
    const items = [{ id: v.id, quantity: S.qty, properties: props }];
    const gw = giftWrapVariant();
    if (S.addon && gw) {
      /* Paper / ribbon are the variant; tag, occasion and paper message come from the wrap overlay
         (snippets/plumplo-wrap-dialog.liquid) as JSON on the same button. Absent = plain add-on. */
      let gp = null;
      try { const b = $('[data-plp-addon]'); gp = b && b.dataset.plpAddonProps ? JSON.parse(b.dataset.plpAddonProps) : null; } catch (e) { gp = null; }
      items.push(gp && Object.keys(gp).length ? { id: gw, quantity: S.qty, properties: gp } : { id: gw, quantity: S.qty });
    }
    clearTimeout(cartT);
    S.cart = 'adding'; error(''); render();
    const designId = S.designId;
    const canonical = canonicalConfig();
    /* Finalisation. The artwork id is stamped on the line first, so the order always
       carries its identity even if the pictures do not make it; the capture is then
       time-boxed and its outcome recorded. Failure never blocks the sale. */
    configHash()
      .then((hash) => {
        const artworkId = designId + '-' + hash;
        props['_artwork_id'] = artworkId;
        return Promise.race([
          captureApproval(artworkId, designId, canonical),
          new Promise((r) => setTimeout(() => r('incomplete'), ARTWORK_TIMEOUT_MS))
        ]);
      }, () => 'incomplete')
      .then((state) => { if (state !== 'complete') props['_artwork_state'] = 'incomplete'; }, () => { props['_artwork_state'] = 'incomplete'; })
      /* Hidden from the customer (underscore); shown to the factory in Eccang. Absent when the
         capture did not reach the service — the order card still has everything. */
      .then(() => { const f = props['_artwork_id'] && filesUrls[props['_artwork_id']]; if (f) props['_Print files'] = f; })
      .then(() => fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections: CART_DRAWER_SECTION }) })
      .then(async (r) => { if (!r.ok) { let m = 'ADD TO CART FAILED'; try { const j = await r.json(); if (j.description) m = String(j.description).toUpperCase(); } catch (e) {} throw new Error(m); } return r.json(); })
      .then((json) => {
        noteCartLines(json && json.items);
        /* The add is confirmed at this point. Only now is the superseded line removed. */
        return replaceEditedLine(json, designId).then((fresh) => {
          S.cart = 'added'; render(); refreshCartCount(); openCartDrawer(fresh || json);
          setTimeout(applyCartPreviews, 400); setTimeout(applyCartPreviews, 1200);
          document.dispatchEvent(new CustomEvent('plumplo:added'));
          cartT = setTimeout(() => { S.cart = 'idle'; render(); }, 2200);
        });
      })
      .catch((err) => { S.cart = 'idle'; error(err.message); render(); }));
  }
  /* Snapshot of the customer's Plumplo for the cart thumbnail, taken from the Creator that is
     already on the page — no second renderer, no service call, nothing leaves the browser.
     A WebGL drawing buffer is only readable in the frame it was drawn, so the copy is made
     inside requestAnimationFrame; the copy is then trimmed to the pillow and scaled to 240px.
     If the frame comes back blank (tab in the background, renderer paused), no preview is
     stored and the cart line keeps Shopify's own product image. */
  const PREVIEW_PX = 240;
  /* A renderer is only worth photographing once it is showing the customer's Plumplo: its
     artwork and outline are built (_img, _geom). Before that it draws the blank ghost, which
     is "painted" but is not what the customer approved (#1005, 11 Oct 2026: both previews
     recorded the ghost while the visible pillow showed the dog). Prefer a running renderer
     with artwork; a ghost is only ever returned when nothing better exists. */
  function artReady(el) {
    try { return !!(el && el._img && el._geom && el._canvas && el._canvas.width); } catch (e) { return false; }
  }
  function liveRenderer() {
    const all = [vis2, vis].filter(Boolean);
    for (const el of all) if (el._running && artReady(el)) return el;
    /* Scrolled out of view the Creator stops its own loop; it can still draw one frame on
       request (drawNow), so a ready renderer is usable even when paused. */
    for (const el of all) if (artReady(el)) return el;
    for (const el of all) if (el._running && el._canvas && el._canvas.width) return el;
    return vis || vis2;
  }
  /* Resolves with a running renderer that has the artwork on it, or null after `frames`
     animation frames (about 60 a second). */
  function readyRenderer(frames) {
    return new Promise((res) => {
      let left = frames == null ? 240 : frames;
      (function tick() {
        const el = liveRenderer();
        if (el && artReady(el) && el._view) return res(el);
        if (--left <= 0) return res(null);
        requestAnimationFrame(tick);
      })();
    });
  }
  function copyFrame() {
    return new Promise((res) => {
      const el = liveRenderer();
      const cv = el && el._canvas;
      if (!cv || !cv.width) return res(null);
      if (!el._running && artReady(el) && typeof el._frame === 'function') {
        try { el._frame(); } catch (e) { return res(null); }
        try {
          const scale = Math.min(1, 600 / Math.max(cv.width, cv.height));
          const t = document.createElement('canvas');
          t.width = Math.max(1, Math.round(cv.width * scale));
          t.height = Math.max(1, Math.round(cv.height * scale));
          t.getContext('2d').drawImage(cv, 0, 0, t.width, t.height);
          return res(t);
        } catch (e) { return res(null); }
      }
      requestAnimationFrame(() => {
        try {
          const scale = Math.min(1, 600 / Math.max(cv.width, cv.height));
          const t = document.createElement('canvas');
          t.width = Math.max(1, Math.round(cv.width * scale));
          t.height = Math.max(1, Math.round(cv.height * scale));
          t.getContext('2d').drawImage(cv, 0, 0, t.width, t.height);
          res(t);
        } catch (e) { res(null); }
      });
    });
  }
  function trimToSquare(src) {
    const w = src.width, h = src.height;
    const d = src.getContext('2d').getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = -1, maxY = -1, seen = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (d[(y * w + x) * 4 + 3] > 12) {
          seen++;
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    if (seen < w * h * 0.01 || maxX < 0) return null;          // blank / near-blank frame
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    const side = Math.max(maxX - minX, maxY - minY) * 1.12;
    const out = document.createElement('canvas');
    out.width = out.height = PREVIEW_PX;
    out.getContext('2d').drawImage(src, cx - side / 2, cy - side / 2, side, side, 0, 0, PREVIEW_PX, PREVIEW_PX);
    return out.toDataURL('image/png');
  }
  let lastPreviewUrl = null;
  function capturePreview(tries) {
    const left = tries == null ? 20 : tries;
    if (left <= 0) return Promise.resolve(null);
    return copyFrame().then((src) => {
      if (!src) return null;
      const url = trimToSquare(src);
      if (url) { lastPreviewUrl = url; return url; }
      return capturePreview(left - 1);
    }, () => null);
  }

  /* ---- artwork capture: the immutable record of what the customer approved ----
     Front and Back Preview are not QA images. They are what the customer signed off,
     and they must still be true months later when someone asks what was ordered.

     `_design_id` cannot carry that on its own: it is minted at upload, before any
     setting exists, so one design can be finalised several different ways. The
     artwork id pins the configuration to the snapshot:

         artwork_id = <design_id>-<config_hash>

     Identical configuration ⇒ identical id ⇒ the same stored assets (the service
     stores write-once, so a repeat is a no-op). Change anything that alters the
     approved result and it is a different id with its own snapshots, and both
     survive independently.

     Capture reuses the technique already proven above for the cart thumbnail: the
     WebGL drawing buffer is only readable in the frame it was drawn, so the copy is
     made inside requestAnimationFrame. The only addition is turning the pillow, which
     is a write to the Creator's private `_view.yaw`. The Creator exposes no public way
     to set the camera — `resetView()` only returns to the front — and it is a frozen
     file, so this is the smallest external seam available. Yaw is read fresh every
     frame, so writing it in one frame and reading the canvas in the next is enough;
     idle auto-rotate drifts about 0.3° in that gap, which does not matter.

     Nothing here can stop a sale. Every failure path ends in `incomplete`, the order
     proceeds, and the service tags it so the gap is conspicuous rather than silent. */

  const ARTWORK_TIMEOUT_MS = 8000;
  const ARTWORK_TRIES = 12;

  /* Only what can change the approved picture. Quantity, the gift-wrap add-on and the
     notes to us are deliberately excluded: none of them alters the artwork. The variant
     id carries size and plumpness in one stable value. Fixed key order, message verbatim
     including its line breaks. */
  function canonicalConfig() {
    const v = variantFor(S.size, S.plump);
    return JSON.stringify({
      v: 1,
      variant: (v && v.id) || null,
      bleedMode: S.bleedMode,
      bleedColor: S.bleedMode === 'none' ? null : String(S.bleedColor || '').toUpperCase(),
      backPrint: !!S.backPrint,
      message: S.showMessage ? String(S.reverseText || '') : '',
      font: S.showMessage && String(S.reverseText || '').trim() ? fontFor(S.msgFont).family : null
    });
  }

  function hex8(buf) {
    const b = new Uint8Array(buf);
    let s = '';
    for (let i = 0; i < 4; i++) s += b[i].toString(16).padStart(2, '0');
    return s;
  }
  /* FNV-1a, used only where SubtleCrypto is unavailable (non-secure context). Still
     deterministic, which is the property that matters here. */
  function fnv1a8(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  }
  function configHash() {
    const json = canonicalConfig();
    const subtle = window.crypto && window.crypto.subtle;
    if (!subtle) return Promise.resolve(fnv1a8(json));
    return subtle.digest('SHA-256', new TextEncoder().encode(json)).then(hex8, () => fnv1a8(json));
  }

  /* A frame is usable only if something was actually drawn. The renderer stops when it
     scrolls out of view or the tab is hidden, and a stopped renderer reads back empty. */
  function framePainted(cv) {
    try {
      const w = cv.width, h = cv.height;
      const d = cv.getContext('2d').getImageData(0, 0, w, h).data;
      let seen = 0;
      for (let i = 3; i < d.length; i += 40) if (d[i] > 12) seen++;
      return seen > (d.length / 40) * 0.01;
    } catch (e) { return false; }
  }

  /* Full resolution, no downscale and no square crop — the cart thumbnail's 240px is
     right for a thumbnail and useless as a record. */
  function grabFace(el, yaw) {
    return new Promise((res) => {
      const cv = el && el._canvas;
      if (!cv || !cv.width || !el._view) return res(null);
      if (!artReady(el)) return res(null);                       // artwork gone mid-capture
      /* Paused renderer (scrolled out of view, e.g. Add to cart from the sticky bar): draw the
         frame ourselves and copy it in the same task, while the drawing buffer still holds it. */
      if (!el._running && typeof el._frame === 'function') {
        try {
          el._view.yaw = yaw;
          el._frame();
          const t = document.createElement('canvas');
          t.width = cv.width; t.height = cv.height;
          t.getContext('2d').drawImage(cv, 0, 0);
          return res(framePainted(t) ? t : null);
        } catch (e) { return res(null); }
      }
      requestAnimationFrame(() => {
        try { el._view.yaw = yaw; } catch (e) { return res(null); }
        requestAnimationFrame(() => {
          try {
            const t = document.createElement('canvas');
            t.width = cv.width; t.height = cv.height;
            t.getContext('2d').drawImage(cv, 0, 0);
            res(framePainted(t) ? t : null);
          } catch (e) { res(null); }
        });
      });
    });
  }
  function grabFaceRetry(el, yaw, tries) {
    const left = tries == null ? ARTWORK_TRIES : tries;
    if (left <= 0) return Promise.resolve(null);
    return grabFace(el, yaw).then((cv) => cv || grabFaceRetry(el, yaw, left - 1), () => null);
  }

  function toPngBlob(cv) {
    return new Promise((res) => {
      try { cv.toBlob((b) => res(b || null), 'image/png'); } catch (e) { res(null); }
    });
  }

  function artworkEndpoint() {
    const e = window.PLUMPLO_BG_ENDPOINT;
    return typeof e === 'string' && /^https?:\/\//.test(e) ? e.replace(/\/+$/, '') : null;
  }
  /* 409 means the service already holds this snapshot — same configuration, same id.
     That is success, not failure. Either answer carries `files_url`, the signed link to this
     artwork's order files page, which goes on the line as `_Print files` for the factory. */
  const filesUrls = {};
  function uploadFace(artworkId, asset, blob, canonical) {
    const base = artworkEndpoint();
    if (!base || !blob) return Promise.resolve(false);
    const fd = new FormData();
    fd.append('image', blob, asset + '.png');
    /* The exact bytes hashed into _artwork_id. Stored beside the previews so the
       approved configuration can be verified later without reconstructing it from
       the order, whose Message property is trimmed. */
    if (canonical) fd.append('config', canonical);
    return fetch(base + '/preview/' + artworkId + '/' + asset, { method: 'POST', body: fd, credentials: 'omit' })
      .then((r) => {
        const ok = r.ok || r.status === 409;
        if (!ok) return false;
        return r.json().then((j) => {
          if (j && typeof j.files_url === 'string' && /^https:\/\//.test(j.files_url)) filesUrls[artworkId] = j.files_url;
          return true;
        }, () => true);
      }, () => false);
  }

  function captureApproval(artworkId, designId, canonical) {
    /* Wait (inside the 8 s budget) for the pillow to carry the photo; never record a ghost. */
    return readyRenderer().then((el) => (el ? captureFrom(el, artworkId, canonical) : 'incomplete'), () => 'incomplete');
  }
  function captureFrom(el, artworkId, canonical) {
    const restore = el._view.yaw;
    const settle = () => {
      try {
        if (typeof el.resetView === 'function') el.resetView();
        else el._view.yaw = restore;
      } catch (e) { /* the customer's view is cosmetic; never fail the sale over it */ }
    };
    return grabFaceRetry(el, 0)
      .then((front) => grabFaceRetry(el, Math.PI).then((back) => {
        settle();
        /* The cart thumbnail comes from the same front grab, so what the customer sees
           in the cart is the picture that was recorded. */
        if (front) {
          try { const u = trimToSquare(front); if (u) { lastPreviewUrl = u; previewSave(artworkId, u); } } catch (e) {}
        }
        if (!front || !back) return 'incomplete';
        return Promise.all([toPngBlob(front), toPngBlob(back)])
          .then(([fb, bb]) => Promise.all([
            uploadFace(artworkId, 'preview-front', fb, canonical),
            uploadFace(artworkId, 'preview-back', bb, canonical)
          ]))
          .then(([a, b]) => (a && b) ? 'complete' : 'incomplete', () => 'incomplete');
      }), () => { settle(); return 'incomplete'; });
  }
  /* Gallery preview: the same snapshot, shown as the first item of the product gallery on the
     left so the customer can still see their Plumplo once the configuration panel has scrolled
     the Creator out of view. It is a picture of the Creator that is already running — no second
     renderer, no second design state, no service call. Refreshed (debounced) whenever a setting
     that changes the render is touched; if a frame cannot be read (Creator paused off-screen)
     the previous picture simply stays. */
  let gT = null, gShown = false;
  function galleryPreviewShow(url, first) {
    if (url) gPreviewThumbs.forEach((im) => { im.src = url; });
    gPreviewEls.forEach((el) => el.removeAttribute('hidden'));
    document.dispatchEvent(new CustomEvent('plumplo:gallery-refresh', { detail: { select: first } }));
  }
  function galleryPreviewHide() {
    if (!gShown) return;
    gShown = false;
    gPreviewEls.forEach((el) => el.setAttribute('hidden', ''));
    document.dispatchEvent(new CustomEvent('plumplo:gallery-refresh', { detail: { select: false } }));
  }
  function galleryPreviewRefresh(delay) {
    if (!isPDP || !gPreviewEls.length) return;
    clearTimeout(gT);
    gT = setTimeout(() => {
      /* While a photo is being processed the slide is deliberately showing the ghost: leave it
         alone rather than hiding it or snapshotting a pillow that has no artwork on it yet. */
      if (S.busy && hasPhoto()) return;
      if (!hasPhoto() || !S.real) { galleryPreviewHide(); return; }
      const first = !gShown;
      if (first) { gShown = true; galleryPreviewShow('', true); mountMirror(); }
      capturePreview(8).then((url) => { if (url) gPreviewThumbs.forEach((im) => { im.src = url; }); });
    }, delay == null ? 550 : delay);
  }
  /* Horizon cart drawer: the layout renders `cart-drawer-section`; its components listen for the
     standard cart:lines-update event carrying the re-rendered section, so we hand them exactly that. */
  const CART_DRAWER_SECTION = 'cart-drawer-section';
  function openCartDrawer(json) {
    try {
      const comp = document.querySelector('cart-drawer-component');
      const ev = new Event('shopify:cart:lines-update', { bubbles: true, cancelable: true });
      Object.assign(ev, { action: 'add', promise: Promise.resolve({ detail: { sections: (json && json.sections) || {}, didError: false } }) });
      document.dispatchEvent(ev);
      const drawer = comp && comp.closest('theme-drawer');
      if (drawer && typeof drawer.open === 'function') setTimeout(() => { if (!drawer.isOpen) drawer.open(); }, 80);
    } catch (e) { /* drawer absent: the cart count still updates */ }
  }
  $$('[data-plp-add]').forEach((b) => on(b, 'click', addToCart));
  $$('[data-plp-remove]').forEach((b) => on(b, 'click', () => removePhoto(b)));

  /* ---- option controls (any section) ----------------------------------------- */
  on(document, 'click', (e) => {
    const t = e.target.closest('[data-plp-size],[data-plp-plump],[data-plp-swatch],[data-plp-noprint],[data-plp-msg],[data-plp-font],[data-plp-addon],[data-plp-qty],[data-plp-sizeref],[data-plp-resetview],[data-plp-sticky-action]');
    if (!t) return;
    if (t.hasAttribute('data-plp-size')) S.size = +t.dataset.plpSize;
    else if (t.hasAttribute('data-plp-plump')) S.plump = +t.dataset.plpPlump;
    else if (t.hasAttribute('data-plp-swatch')) { const h = t.dataset.plpSwatch; if (h === 'none') S.bleedMode = 'none'; else { S.bleedColor = h; S.bleedMode = 'color'; } }
    else if (t.hasAttribute('data-plp-noprint')) S.backPrint = !S.backPrint;
    else if (t.hasAttribute('data-plp-msg')) S.showMessage = !S.showMessage;
    else if (t.hasAttribute('data-plp-font')) { S.msgFont = t.dataset.plpFont; ensureFont(S.msgFont); }
    else if (t.hasAttribute('data-plp-addon')) S.addon = !S.addon;
    else if (t.hasAttribute('data-plp-qty')) S.qty = Math.max(1, Math.min(20, S.qty + (+t.dataset.plpQty)));
    else if (t.hasAttribute('data-plp-sizeref')) S.sizeRef = !S.sizeRef;
    else if (t.hasAttribute('data-plp-resetview')) { if (vis && vis.resetView) vis.resetView(); return; }
    else if (t.hasAttribute('data-plp-sticky-action')) { if (hasPhoto()) addToCart(); else if (!openPicker()) { if (isPDP) toUpload(); else scrollToId('buy'); } return; }
    render();
    galleryPreviewRefresh();
  });
  const hexIns = $$('[data-plp-hex]');
  const colorIns = $$('[data-plp-color]');
  const onHex = (e) => { const v = e.target.value; if (/^#[0-9a-f]{6}$/i.test(v) || v === '') { S.bleedColor = v || '#FFFFFF'; S.bleedMode = 'color'; render(); } };
  hexIns.forEach((el) => on(el, 'input', onHex)); colorIns.forEach((el) => on(el, 'input', onHex));
  hexIns.concat(colorIns).forEach((el) => on(el, 'input', () => galleryPreviewRefresh(800)));
  const msgIns = $$('[data-plp-message]');
  const notesIns = $$('[data-plp-notes]');
  msgIns.forEach((el) => on(el, 'input', (e) => { autogrow(e.target); S.reverseText = e.target.value; visAttrs(); render(); galleryPreviewRefresh(900); }));
  notesIns.forEach((el) => on(el, 'input', (e) => { autogrow(e.target); S.notes = e.target.value; render(); }));

  /* ---- carousel --------------------------------------------------------------- */
  const slides = carousel ? $$('[data-plp-slide]', carousel) : [];
  const dots = $$('[data-plp-dot]');
  let holdT = null;
  const hold = () => { clearTimeout(holdT); S.held = true; };
  const release = () => { clearTimeout(holdT); holdT = setTimeout(() => { S.held = false; }, 1200); };
  const go = (d) => { const n = slides.length; if (!n) return; S.slide = (S.slide + d + n) % n; renderCarousel(); };
  function renderCarousel() {
    const n = slides.length;
    slides.forEach((el, i) => {
      let rel = i - S.slide; if (rel > n / 2) rel -= n; if (rel < -n / 2) rel += n;
      const a = Math.abs(rel), hidden = a > 1, scale = rel === 0 ? 1 : 0.68, x = rel === 0 ? 0 : rel * 52;
      el.style.transform = 'translateX(-50%) translateX(' + x + '%) scale(' + (hidden ? 0.5 : scale) + ')';
      el.style.opacity = hidden ? 0 : rel === 0 ? 1 : 0.55;
      el.style.zIndex = 3 - a;
    });
    dots.forEach((d, i) => d.setAttribute('aria-selected', S.slide === i ? 'true' : 'false'));
  }
  if (carousel) {
    on($('[data-plp-prev]'), 'click', () => { hold(); go(-1); release(); });
    on($('[data-plp-next]'), 'click', () => { hold(); go(1); release(); });
    dots.forEach((d, i) => on(d, 'click', () => { hold(); S.slide = i; renderCarousel(); release(); }));
    on(carousel, 'keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); hold(); go(-1); release(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); hold(); go(1); release(); }
    });
    let sx = null;
    on(carousel, 'pointerdown', (e) => { sx = e.clientX; hold(); });
    on(carousel, 'pointermove', (e) => { if (sx == null) return; const dx = e.clientX - sx; if (Math.abs(dx) > 45) { go(dx < 0 ? 1 : -1); sx = e.clientX; } });
    const up = () => { sx = null; release(); };
    on(carousel, 'pointerup', up); on(carousel, 'pointercancel', up);
    on(carousel, 'mouseenter', hold); on(carousel, 'mouseleave', release);
    on(carousel, 'focus', hold); on(carousel, 'blur', release);
    const reduced = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    setInterval(() => { if (S.held || hasPhoto() || reduced || document.hidden) return; go(1); }, 5200);
    renderCarousel();
  }

  /* ---- sticky bar ------------------------------------------------------------- */
  const stickyBar = $('[data-plp-sticky]');
  const stickyAnchor = $('[data-plp-sticky-anchor]');
  /* The bar arms once the main Add to Cart has left the viewport and stands down when it returns.
     The two thresholds are deliberately a few pixels apart: a single boundary flickers on and off
     while momentum scrolling hovers on it. */
  const STICKY_HYST = 8;
  function checkSticky() {
    if (!stickyBar || !stickyAnchor) return;
    const b = stickyAnchor.getBoundingClientRect().bottom;
    const s = S.sticky ? b < STICKY_HYST : b < -STICKY_HYST;
    if (s !== S.sticky) { S.sticky = s; stickyBar.classList.toggle('is-on', s); }
  }
  on(window, 'scroll', checkSticky, { passive: true });
  /* ...and on the real scroll container, for the same reason the anchor links needed it. */
  (function () {
    const box = scrollBox();
    if (box && box !== document.scrollingElement && box !== document.documentElement) on(box, 'scroll', checkSticky, { passive: true });
  })();

  /* ---- render ------------------------------------------------------------------ */
  function setPressed(sel, isOn) { $$(sel).forEach((b) => b.setAttribute('aria-pressed', isOn(b) ? 'true' : 'false')); }
  function setText(sel, txt) { $$(sel).forEach((el) => { el.textContent = txt; }); }
  /* CTA label centred on the whole button, price laid over its left edge (out of flow, so the
     label never shifts with the price's length). Falls back to plain text with no price. */
  function setCta(btn, priceTxt, label) {
    let p = $('.plp-cta__price', btn), l = $('.plp-cta__label', btn);
    if (!l) { btn.textContent = ''; l = document.createElement('span'); l.className = 'plp-cta__label'; btn.appendChild(l); }
    if (l.textContent !== label) l.textContent = label;
    if (priceTxt) {
      if (!p) { p = document.createElement('span'); p.className = 'plp-cta__price'; p.setAttribute('aria-hidden', 'false'); btn.insertBefore(p, l); }
      if (p.textContent !== priceTxt) p.textContent = priceTxt;
    } else if (p) { p.remove(); }
  }
  function show(sel, onOff) { $$(sel).forEach((el) => el.classList.toggle('plp-hidden', !onOff)); }

  function render() {
    const photo = hasPhoto();
    const sz = sizes[S.size] || {};
    if (hero) hero.dataset.photo = photo ? '1' : '0';
    show('[data-plp-if-photo]', photo);
    show('[data-plp-if-nophoto]', !photo);

    setPressed('[data-plp-size]', (b) => +b.dataset.plpSize === S.size);
    setPressed('[data-plp-plump]', (b) => +b.dataset.plpPlump === S.plump);
    setPressed('[data-plp-swatch]', (b) => { const h = b.dataset.plpSwatch; return h === 'none' ? S.bleedMode === 'none' : (S.bleedMode === 'color' && S.bleedColor.toLowerCase() === h.toLowerCase()); });
    setPressed('[data-plp-noprint]', () => !S.backPrint);
    setPressed('[data-plp-msg]', () => S.showMessage);
    setPressed('[data-plp-font]', (b) => b.dataset.plpFont === S.msgFont);
    document.documentElement.style.setProperty('--plp-msgfont', fontFor(S.msgFont).family);
    setPressed('[data-plp-addon]', () => S.addon);
    setPressed('[data-plp-sizeref]', () => S.sizeRef);
    hexIns.forEach((el) => { if (document.activeElement !== el) el.value = S.bleedColor; });
    if (/^#[0-9a-f]{6}$/i.test(S.bleedColor)) colorIns.forEach((el) => { el.value = S.bleedColor; });
    show('[data-plp-if-msg]', S.showMessage);
    msgIns.forEach((el) => { if (el.value !== S.reverseText) el.value = S.reverseText; autogrow(el); });
    notesIns.forEach((el) => { if (el.value !== S.notes) el.value = S.notes; autogrow(el); });
    setText('[data-plp-qty-out]', String(S.qty));
    setText('[data-plp-sizeref-label]', S.sizeRef ? 'Hide size guide' : 'Show size guide');
    show('[data-plp-phone]', S.sizeRef);

    /* phone for scale: a handset is ~15cm tall, so its height is that fraction of the pillow's */
    const cm = sz.cm || 30;
    const pct = Math.max(6, Math.min(80, 74 * 15 / cm));
    $$('[data-plp-phone]').forEach((ph) => {
      ph.style.height = pct.toFixed(1) + '%';
      const stageH = mount ? mount.getBoundingClientRect().height || 420 : 420;
      const fs = Math.max(3.5, Math.min(9, stageH * pct / 100 * 0.0386));
      const cap = $('.plp-phone__screen span', ph); if (cap) cap.style.fontSize = fs.toFixed(1) + 'px';
    });
    /* size scene: sofa is 220cm wide, so the pillow is that fraction of the sofa's width */
    $$('[data-plp-scene-pillow]').forEach((im) => { im.style.width = (cm / 220 * 100).toFixed(1) + '%'; });

    const price = priceLabel();
    setText('[data-plp-price]', price);
    /* Product page: the selected variant's Shopify price leads the label ("$XX.XX · Add to cart");
       an unavailable combination shows no price (the click keeps its existing error). */
    const cur = variantFor(S.size, S.plump);
    const showPrice = isPDP && S.cart === 'idle' && (placeholder || (cur && cur.available));
    const showCtaPrice = S.cart === 'idle' && (placeholder || (cur && cur.available));
    const ctaLabel = S.cart === 'adding' ? (isPDP ? 'Adding…' : 'One moment…') : S.cart === 'added' ? 'Added to cart ✓' : isPDP ? (cfg.ctaAdd || 'Add to cart') : (cfg.ctaCreate || 'Create my Plumplo');
    $$('[data-plp-add]').forEach((b) => { setCta(b, showCtaPrice ? price : '', ctaLabel); b.dataset.state = S.cart; });
    setText('[data-plp-upload-label]', photo ? (cfg.uploadAttached || 'Change photo') : (cfg.uploadLabel || 'Upload your photo'));
    setText('[data-plp-upload-action]', photo ? 'CHANGE' : 'CHOOSE FILE');

    /* buy-box stage + sticky thumb: the customer's photo in a soft blob, or an example */
    $$('[data-plp-photo-img]').forEach((im) => { if (photo) im.src = S.photo; });

    setText('[data-plp-sticky-title]', (photo ? 'Your Plumplo' : (cfg.stickyTitle || 'Custom photo pillow')) + ' · ' + (sz.name || ''));
    setText('[data-plp-sticky-price]', placeholder ? price + ' · VARIANT PRICE' : price);
    $$('[data-plp-sticky-action]').forEach((b) => {
      const lbl = photo ? (S.cart === 'added' ? 'Added ✓' : (isPDP ? (cfg.ctaAdd || 'Add to cart') : 'Create')) : 'Upload';
      setCta(b, (photo && showCtaPrice) ? price : '', lbl);
    });

    visAttrs();
    checkSticky();
  }
  render();
  on(window, 'resize', render, { passive: true });
  let saveT = null;
  on(document, 'click', () => { if (S.designId && cutoutBlob) { clearTimeout(saveT); saveT = setTimeout(saveDesign, 400); } });
  on(document, 'input', () => { if (S.designId && cutoutBlob) { clearTimeout(saveT); saveT = setTimeout(saveDesign, 600); } });
  resumeDesign();
})();